import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Instagram,
  Facebook,
  Phone,
  Mail,
  Split,
  ScrollText,
  AlertCircle,
  Info,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  Package,
  TrendingUp,
  CheckCircle2,
  Archive,
  Tag,
  Store,
  Globe,
  Calendar,
  User,
  Key,
} from "lucide-react";
import moment from "moment";

const RedemptionDealDetailsSlider = ({ isOpen, onClose, redemptionData }) => {
  const [activeTab, setActiveTab] = useState("details");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showImageGallery, setShowImageGallery] = useState(false);

  // Extract deal data from redemptionData.deal_summary
  const dealSummary = redemptionData?.deal_summary;
  
  // Parse partner_location if it's a JSON string (like in ViewOffer)
  let partnerLocationData = dealSummary?.partner_location;
  if (typeof dealSummary?.partner_location === "string") {
    try {
      partnerLocationData = JSON.parse(dealSummary.partner_location);
    } catch (error) {
      partnerLocationData = null;
    }
  }
  
  // Transform API data to match expected structure
  const deal = dealSummary ? {
    title: dealSummary.title || "Deal Title Not Available",
    image: dealSummary.image || "https://placehold.co/500x500/FF6C6C/FFF?text=No+Image",
    images: dealSummary.images || [],
    status: dealSummary.status || "Unknown",
    price: dealSummary.price,
    discounted_price: dealSummary.discounted_price,
    discount_percentage: dealSummary.discount_percentage,
    quantity: dealSummary.quantity,
    category: dealSummary.category || [],
    expiry_date: dealSummary.expiry_date,
    validity_type: dealSummary.validity_type || "regular",
    gender: dealSummary.gender || "All Genders",
    details: {
      description: dealSummary.description || "No description available.",
      redemption_instructions: dealSummary.redemption_instructions || dealSummary.terms_and_conditions || "No redemption instructions provided.",
    },
    redemption_type: dealSummary.redemption_type || [],
    contact: {
      phone_number: dealSummary.deals_phone_number || dealSummary.vendor_phone_number,
      email: dealSummary.email,
      social_media: {
        instagram: dealSummary.instagram,
        facebook: dealSummary.facebook,
        twitter: dealSummary.twitter,
      },
      website: dealSummary.website,
    },
    brand_name: dealSummary.brand_name,
    brand_category: dealSummary.brand_category,
    logo: dealSummary.logo,
    region_location: dealSummary.region_location || [],
    redemption_region_location: dealSummary.redemption_region_location || [],
    partner_location: partnerLocationData,
    target_branches: dealSummary.target_branches || [],
    target_partners: dealSummary.target_partners || [],
    redemption_branches: dealSummary.redemption_branches || [],
    redemption_partners: dealSummary.redemption_partners || [],
    // Handle age as array - extract first item if array, otherwise use as is
    age: Array.isArray(dealSummary.age) && dealSummary.age.length > 0 
      ? dealSummary.age[0] 
      : dealSummary.age,
    redemption_limits: dealSummary.redemption_limits || [],
    terms: dealSummary.terms_and_conditions || "No terms and conditions available.",
    refund_policy: dealSummary.refund_policy || "No refund policy specified.",
    offer_type: dealSummary.offer_type,
    sku: dealSummary.sku,
    properties: dealSummary.properties || [],
    exclusions: dealSummary.exclusions || [],
    delivery_fee: dealSummary.delivery_fee,
    free_delivery_areas: dealSummary.free_delivery_areas || [],
    // Handle both array and single object formats (like ViewOffer)
    bundle: Array.isArray(dealSummary.bundle) ? dealSummary.bundle : (dealSummary.bundle ? [dealSummary.bundle] : []),
    bogo: Array.isArray(dealSummary.bogo) ? dealSummary.bogo : (dealSummary.bogo ? [dealSummary.bogo] : []),
    flash: Array.isArray(dealSummary.flash) ? dealSummary.flash : (dealSummary.flash ? [dealSummary.flash] : []),
  } : null;

  // Helper function to check if deal is expired
  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return moment(expiryDate).isBefore(moment());
  };

  // Helper function to check if flash sale is expired (from ViewOffer)
  const isFlashSaleExpired = (flashData) => {
    if (!flashData) return false;
    const flashArray = Array.isArray(flashData) ? flashData : [flashData];
    return flashArray.some(
      (flash) =>
        flash &&
        flash.flash_end_time &&
        moment(flash.flash_end_time).isBefore(moment())
    );
  };

  // Helper function to check if flash sale is active (from ViewOffer)
  const isFlashSaleActive = (flashData) => {
    if (!flashData) return false;
    const flashArray = Array.isArray(flashData) ? flashData : [flashData];
    return flashArray.some(
      (flash) =>
        flash &&
        flash.flash_start_time &&
        flash.flash_end_time &&
        moment().isBetween(
          moment(flash.flash_start_time),
          moment(flash.flash_end_time)
        )
    );
  };

  // Helper function to check if BOGO has valid data (from ViewOffer)
  const hasBogoData = (bogo) => {
    if (!bogo) return false;
    const bogoData = Array.isArray(bogo) ? bogo[0] : bogo;
    return (
      bogoData &&
      (bogoData.bogo_item_price ||
        bogoData.item_price ||
        (bogoData.bogo_quantity && bogoData.bogo_free_quantity) ||
        (bogoData.quantity && bogoData.free_quantity))
    );
  };

  // Helper function to check if Bundle has valid data (from ViewOffer)
  const hasBundleData = (bundle) => {
    if (!bundle) return false;
    const bundleData = Array.isArray(bundle) ? bundle[0] : bundle;
    return (
      bundleData &&
      bundleData.original_sum &&
      bundleData.bundle_price &&
      bundleData.bundle_savings
    );
  };

  // Helper functions for image gallery
  const getAllImages = () => {
    const images = [];
    if (deal?.image) {
      images.push(deal.image);
    }
    if (deal?.images && deal.images.length > 0) {
      images.push(...deal.images);
    }
    return images;
  };

  const nextImage = () => {
    const images = getAllImages();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    const images = getAllImages();
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const openImageGallery = (index = 0) => {
    setCurrentImageIndex(index);
    setShowImageGallery(true);
  };

  // If no deal data, show error state
  if (!deal && isOpen) {
    return (
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 z-[1000] backdrop-blur-sm"
              onClick={onClose}
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 bottom-0 z-[1001] w-full md:w-[600px] bg-white shadow-2xl flex flex-col"
            >
              <div className="flex justify-between items-center p-6 border-b">
                <h2 className="text-xl font-semibold text-gray-900">Deal Details</h2>
                <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <div className="flex-1 flex items-center justify-center p-6">
                <div className="text-center">
                  <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Deal Data Available</h3>
                  <p className="text-sm text-gray-600">Unable to load deal information. Please try again.</p>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/40 z-[1000] backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Slider Panel */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 bottom-0 z-[1001] w-full md:w-[95%] lg:w-[90%] xl:w-[85%] 2xl:w-[80%]
                      bg-white shadow-2xl flex flex-col overflow-hidden max-w-[1800px]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex-shrink-0 border-b bg-white">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 p-4 md:p-6">
                <div className="flex-1 min-w-0 w-full sm:w-auto">
                  <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 mb-2">
                    Deal Details
                  </h2>
                  {redemptionData && (
                    <div className="flex flex-wrap gap-x-2 gap-y-1 items-center text-xs sm:text-sm text-gray-600">
                      <div className="flex items-center gap-1 min-w-0">
                        <User className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0" />
                        <span className="truncate max-w-[150px] sm:max-w-none">{redemptionData.customer_name}</span>
                      </div>
                      <span className="text-gray-300 hidden xs:inline">•</span>
                      <div className="flex items-center gap-1">
                        <Key className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0" />
                        <span className="font-mono text-[10px] sm:text-xs">{redemptionData.redemption_code}</span>
                      </div>
                      <span className="text-gray-300 hidden xs:inline">•</span>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-[10px] sm:text-xs">{moment(redemptionData.updated_at).format("DD MMM YYYY")}</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 items-center self-end sm:self-auto">
                  <span
                    className={`px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium text-white rounded-full whitespace-nowrap ${
                      isExpired(deal.expiry_date)
                        ? "bg-red-500"
                        : deal.status === "Advertise"
                        ? "bg-purple-500"
                        : deal.status === "Pending"
                        ? "bg-orange-500"
                        : deal.status === "Approved"
                        ? "bg-green-500"
                        : "bg-red-500"
                    }`}
                  >
                    {isExpired(deal.expiry_date) ? "Expired" : deal.status}
                  </span>
                  <button
                    onClick={onClose}
                    className="p-1.5 sm:p-2 rounded-xl transition-colors duration-200 hover:bg-gray-100 flex-shrink-0"
                  >
                    <X className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                  </button>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-grow overflow-y-auto p-3 sm:p-4 md:p-6">
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
                {/* Left Column - Image */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="xl:sticky xl:top-0 h-fit"
                >
                  <div className="flex flex-col overflow-hidden justify-center items-center p-4 sm:p-6 bg-purple-100 rounded-2xl min-h-[280px] sm:min-h-[350px]">
                    <div className="relative flex-1 w-full max-w-md">
                      <img
                        src={deal?.image || "https://via.placeholder.com/400"}
                        alt="Deal"
                        className="w-full h-full max-h-[280px] rounded-xl cursor-pointer hover:opacity-90 transition-opacity object-cover"
                        onClick={() => openImageGallery(0)}
                      />

                      {/* Image Gallery Indicator */}
                      {getAllImages().length > 1 && (
                        <div className="flex absolute top-2 right-2 gap-1 items-center px-2 py-1 text-xs text-white rounded-full bg-black/50">
                          <ImageIcon className="w-3 h-3" />
                          <span>{getAllImages().length}</span>
                        </div>
                      )}
                    </div>

                    {/* Additional Images Thumbnails */}
                    {deal?.images && deal.images.length > 0 && (
                      <div className="mt-4 w-full">
                        <div className="flex overflow-x-auto gap-3 pb-2 max-w-full">
                          {deal.images.slice(0, 5).map((image, index) => (
                            <img
                              key={index}
                              src={image}
                              alt={`Additional ${index + 1}`}
                              className="object-cover flex-shrink-0 w-20 h-20 rounded-lg border-2 border-gray-200 transition-opacity cursor-pointer hover:opacity-80 hover:border-purple-300"
                              onClick={() => openImageGallery(index + 1)}
                            />
                          ))}
                          {deal.images.length > 5 && (
                            <div
                              className="flex flex-shrink-0 justify-center items-center w-20 h-20 bg-gray-200 rounded-lg border-2 border-gray-200 transition-colors cursor-pointer hover:bg-gray-300 hover:border-purple-300"
                              onClick={() => openImageGallery(0)}
                            >
                              <span className="text-sm font-medium text-gray-600">
                                +{deal.images.length - 5}
                              </span>
                            </div>
                          )}
                        </div>
                        {deal.images.length > 1 && (
                          <p className="mt-2 text-xs text-center text-gray-500">
                            Click any image to view full gallery
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </motion.div>

                {/* Right Column - Details */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex flex-col space-y-4 md:space-y-6"
                >
                  {/* Vendor Branding Section */}
                  {deal?.brand_name && (
                    <div className="p-4 bg-gradient-to-r from-pink-50 via-purple-50 to-indigo-50 rounded-2xl border border-pink-200">
                      <div className="flex items-center gap-3">
                        {deal?.logo && (
                          <img
                            src={deal.logo}
                            alt={deal.brand_name}
                            className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-md"
                            onError={(e) => {
                              e.target.src = "https://placehold.co/48x48/FF6C6C/FFF?text=" + deal.brand_name?.charAt(0);
                            }}
                          />
                        )}
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-gray-900">{deal.brand_name}</h4>
                          {deal.brand_category && (
                            <p className="text-xs text-gray-600">{deal.brand_category}</p>
                          )}
                        </div>
                        {deal.offer_type && (
                          <span className="px-3 py-1 text-xs font-medium bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-full">
                            {deal.offer_type.charAt(0).toUpperCase() + deal.offer_type.slice(1)}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 line-clamp-2">
                    {deal?.title}
                  </h3>

                  {/* Tabs Navigation */}
                  <div className="flex overflow-x-auto space-x-0.5 sm:space-x-1 border-b scrollbar-hide -mx-2 px-2">
                    {[
                      { id: "details", label: "Details" },
                      { id: "categories", label: "Categories" },
                      { id: "contact", label: "Contact" },
                      { id: "locations", label: "Locations" },
                      { id: "redemption", label: "Redemption" },
                      { id: "terms", label: "T&Cs" },
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-2 sm:px-3 md:px-4 py-2 text-[11px] sm:text-xs md:text-sm font-medium whitespace-nowrap transition-colors duration-200 flex-shrink-0
                          ${
                            activeTab === tab.id
                              ? "text-pink-600 border-b-2 border-pink-600"
                              : "text-gray-600 hover:text-pink-600"
                          }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Scrollable Tab Content */}
                  <div className="overflow-y-auto flex-1 -mr-2">
                    <div className="pr-2 space-y-3 sm:space-y-4">
                      {activeTab === "details" && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="space-y-4"
                        >
                          {/* SKU & Properties */}
                          {(deal.sku || (deal.properties && deal.properties.length > 0)) && (
                            <div className="p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-200">
                              {deal.sku && (
                                <div className="mb-3">
                                  <span className="text-xs font-medium text-blue-600">SKU: </span>
                                  <span className="text-sm font-mono font-semibold text-blue-900">{deal.sku}</span>
                                </div>
                              )}
                              {deal.properties && deal.properties.length > 0 && (
                                <div className="grid grid-cols-2 gap-2">
                                  {deal.properties.map((prop, idx) => (
                                    <div key={idx} className="p-2 bg-white rounded-lg border border-blue-100">
                                      <p className="text-xs text-gray-500">{prop.name}</p>
                                      <p className="text-sm font-semibold text-gray-900">{prop.value}</p>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Price Information - Smart offer type detection (from ViewOffer) */}
                          {(() => {
                            // Determine offer type based on data structure
                            const offerType =
                              deal.offer_type ||
                              (hasBundleData(deal.bundle)
                                ? "bundle"
                                : hasBogoData(deal.bogo)
                                ? "bogo"
                                : deal.discounted_price && deal.discounted_price !== 0
                                ? "discount"
                                : "standard");

                            // Only show pricing for discount offers or when no special offer type
                            if (
                              offerType === "discount" ||
                              (offerType === "standard" && deal.price && deal.price !== 0)
                            ) {
                              const originalPrice = deal.price;
                              const discountedPrice = deal.discounted_price || deal.discountedPrice;
                              const discountPercentage = deal.discount_percentage || deal.discountPercentage;

                              // Calculate discount percentage if not provided
                              const calculatedDiscount =
                                discountedPrice && originalPrice
                                  ? Math.round(((originalPrice - discountedPrice) / originalPrice) * 100)
                                  : discountPercentage;

                              return (
                                <div className="p-4 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 rounded-xl border border-green-200 shadow-sm">
                                  <div className="flex items-center gap-2 mb-3">
                                    <Tag className="w-4 h-4 text-green-600" />
                                    <h4 className="text-sm font-semibold text-gray-900">Pricing Details</h4>
                                  </div>
                                  <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 gap-3">
                                    <div className="p-3 bg-white rounded-lg border border-green-100">
                                      <span className="text-xs text-gray-500">Original Price</span>
                                      <div className="text-lg sm:text-xl font-bold text-gray-900">
                                        ₵{parseFloat(originalPrice).toFixed(2)}
                                      </div>
                                    </div>
                                    {discountedPrice && discountedPrice !== 0 && (
                                      <div className="p-3 bg-white rounded-lg border border-green-100">
                                        <span className="text-xs text-gray-500">Discounted Price</span>
                                        <div className="text-lg sm:text-xl font-bold text-green-600">
                                          ₵{parseFloat(discountedPrice).toFixed(2)}
                                        </div>
                                      </div>
                                    )}
                                    {calculatedDiscount && calculatedDiscount > 0 && (
                                      <div className="p-3 bg-gradient-to-br from-pink-100 to-purple-100 rounded-lg border border-pink-200">
                                        <span className="text-xs text-pink-600">You Save</span>
                                        <div className="text-lg sm:text-xl font-bold text-pink-600">
                                          {calculatedDiscount}%
                                        </div>
                                        <span className="text-xs text-pink-500">
                                          ₵{(parseFloat(originalPrice) - parseFloat(discountedPrice || originalPrice)).toFixed(2)} off
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            }
                            return null;
                          })()}

                          {/* Bundle Offer Information (from ViewOffer logic) */}
                          {hasBundleData(deal.bundle) && (
                            <div className="p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-100">
                              <h4 className="mb-2 text-sm font-medium text-purple-700">
                                Bundle Offer Details
                              </h4>
                              {(() => {
                                // Handle both array and object formats
                                const bundleData = Array.isArray(deal.bundle) ? deal.bundle : [deal.bundle];
                                return bundleData.map((bundleItem, index) => (
                                  <div key={index} className="space-y-2">
                                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                                      <div className="p-2 bg-white rounded border">
                                        <span className="text-xs text-gray-500">Original Sum</span>
                                        <div className="text-lg font-bold text-gray-900">
                                          ₵{parseFloat(bundleItem.original_sum).toFixed(2)}
                                        </div>
                                      </div>
                                      <div className="p-2 bg-white rounded border">
                                        <span className="text-xs text-gray-500">Bundle Price</span>
                                        <div className="text-lg font-bold text-green-600">
                                          ₵{parseFloat(bundleItem.bundle_price).toFixed(2)}
                                        </div>
                                      </div>
                                      <div className="p-2 bg-white rounded border">
                                        <span className="text-xs text-gray-500">Total Savings</span>
                                        <div className="text-lg font-bold text-pink-600">
                                          ₵{parseFloat(bundleItem.bundle_savings).toFixed(2)}
                                        </div>
                                      </div>
                                    </div>
                                    {bundleItem.bundle_skus && bundleItem.bundle_skus.length > 0 && (
                                      <div className="mt-2">
                                        <span className="text-xs text-gray-500">Included Items:</span>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                          {bundleItem.bundle_skus.map((sku, skuIndex) => (
                                            <span
                                              key={skuIndex}
                                              className="px-2 py-1 text-xs text-purple-700 bg-purple-100 rounded"
                                            >
                                              {sku.product_name} ({sku.sku})
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ));
                              })()}
                            </div>
                          )}

                          {/* BOGO Offer Information (from ViewOffer logic) */}
                          {hasBogoData(deal.bogo) && (
                            <div className="p-3 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg border border-blue-100">
                              <h4 className="mb-2 text-sm font-medium text-blue-700">
                                Buy One Get One Offer
                              </h4>
                              {(() => {
                                // Handle both array and object formats
                                const bogoData = Array.isArray(deal.bogo) ? deal.bogo : [deal.bogo];
                                return bogoData.map((bogoItem, index) => (
                                  <div key={index} className="space-y-2">
                                    {(bogoItem.bogo_item_price || bogoItem.item_price) && (
                                      <div className="p-2 bg-white rounded border">
                                        <span className="text-xs text-gray-500">Item Price</span>
                                        <div className="text-lg font-bold text-blue-600">
                                          ₵{parseFloat(bogoItem.bogo_item_price || bogoItem.item_price).toFixed(2)}
                                        </div>
                                      </div>
                                    )}
                                    {((bogoItem.bogo_quantity && bogoItem.bogo_free_quantity) ||
                                      (bogoItem.quantity && bogoItem.free_quantity)) && (
                                      <div className="text-sm text-gray-600">
                                        Buy {bogoItem.bogo_quantity || bogoItem.quantity} get{" "}
                                        {bogoItem.bogo_free_quantity || bogoItem.free_quantity} free
                                      </div>
                                    )}
                                  </div>
                                ));
                              })()}
                            </div>
                          )}

                          {/* Flash Sale Info */}
                          {deal.validity_type === "flash_sale" && deal.flash && (
                            <div className="p-4 bg-gradient-to-br from-red-50 to-orange-50 rounded-xl border border-red-200 shadow-sm animate-pulse">
                              <div className="flex items-center gap-2 mb-2">
                                <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M13 10V3L4 14h7v7l9-11h-7z" clipRule="evenodd" />
                                </svg>
                                <h4 className="text-sm font-semibold text-red-900">⚡ Flash Sale Deal</h4>
                              </div>
                              <p className="text-xs text-red-600">Limited time offer - Act fast!</p>
                            </div>
                          )}

                          {/* Quantity Available Information (from ViewOffer with full animations) */}
                          {(deal.quantity !== null && deal.quantity !== undefined) || deal.quantity === 0 ? (
                            <div className="p-4 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-xl border border-indigo-200 shadow-sm">
                              <div className="flex gap-3 items-center mb-4">
                                <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-md">
                                  <Package className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                  <h4 className="text-sm font-semibold text-gray-900">
                                    Quantity Available
                                  </h4>
                                  <p className="text-xs text-gray-500">Items in stock for this deal</p>
                                </div>
                              </div>

                              {(() => {
                                // null/undefined = unlimited, 0 = out of stock, number = actual quantity
                                const quantity =
                                  deal.quantity === null || deal.quantity === undefined
                                    ? null
                                    : Number(deal.quantity) || 0;

                                // Determine stock status
                                const getStockStatus = () => {
                                  if (quantity === null) {
                                    return {
                                      text: "Unlimited",
                                      color: "brand",
                                      icon: Package,
                                      bgColor: "from-[#FF6C6C]/10 to-pink-50",
                                      borderColor: "border-[#FF6C6C]/20",
                                      textColor: "text-[#FF6C6C]",
                                      iconBg: "bg-[#FF6C6C]",
                                    };
                                  }
                                  if (quantity === 0) {
                                    return {
                                      text: "Out of Stock",
                                      color: "red",
                                      icon: AlertCircle,
                                      bgColor: "from-red-50 to-red-100",
                                      borderColor: "border-red-200",
                                      textColor: "text-red-700",
                                      iconBg: "bg-red-500",
                                    };
                                  }
                                  if (quantity < 10) {
                                    return {
                                      text: "Low stock",
                                      color: "orange",
                                      icon: AlertCircle,
                                      bgColor: "from-orange-50 to-orange-100",
                                      borderColor: "border-orange-200",
                                      textColor: "text-orange-700",
                                      iconBg: "bg-orange-500",
                                    };
                                  }
                                  if (quantity < 50) {
                                    return {
                                      text: "Moderate stock",
                                      color: "yellow",
                                      icon: Archive,
                                      bgColor: "from-yellow-50 to-yellow-100",
                                      borderColor: "border-yellow-200",
                                      textColor: "text-yellow-700",
                                      iconBg: "bg-yellow-500",
                                    };
                                  }
                                  if (quantity < 200) {
                                    return {
                                      text: "Good stock",
                                      color: "green",
                                      icon: CheckCircle2,
                                      bgColor: "from-green-50 to-green-100",
                                      borderColor: "border-green-200",
                                      textColor: "text-green-700",
                                      iconBg: "bg-green-500",
                                    };
                                  }
                                  return {
                                    text: "Excellent stock",
                                    color: "blue",
                                    icon: TrendingUp,
                                    bgColor: "from-blue-50 to-blue-100",
                                    borderColor: "border-blue-200",
                                    textColor: "text-blue-700",
                                    iconBg: "bg-blue-500",
                                  };
                                };

                                const stockStatus = getStockStatus();
                                const StatusIcon = stockStatus.icon;

                                return (
                                  <div
                                    className={`p-4 rounded-xl border-2 bg-gradient-to-br ${stockStatus.bgColor} ${stockStatus.borderColor}`}
                                  >
                                    <div className="flex justify-between items-center">
                                      <div className="flex gap-3 items-center">
                                        <motion.div
                                          className={`p-3 rounded-xl shadow-md ${stockStatus.iconBg}`}
                                          animate={{ scale: [1, 1.05, 1] }}
                                          transition={{
                                            duration: 2,
                                            repeat: Infinity,
                                            repeatType: "reverse",
                                          }}
                                        >
                                          <StatusIcon className="w-6 h-6 text-white" />
                                        </motion.div>
                                        <div>
                                          <p className="mb-1 text-xs font-medium text-gray-600">
                                            Stock Status
                                          </p>
                                          <p className={`text-lg font-bold ${stockStatus.textColor}`}>
                                            {stockStatus.text}
                                          </p>
                                        </div>
                                      </div>

                                      <div className="text-right">
                                        <p className="mb-1 text-xs font-medium text-gray-600">
                                          Total Items
                                        </p>
                                        <motion.p
                                          className={`text-3xl font-bold ${stockStatus.textColor}`}
                                          initial={{ scale: 1.2, opacity: 0 }}
                                          animate={{ scale: 1, opacity: 1 }}
                                          transition={{ type: "spring", stiffness: 300 }}
                                        >
                                          {quantity === null
                                            ? "∞"
                                            : quantity === 0
                                            ? "0"
                                            : quantity.toLocaleString()}
                                        </motion.p>
                                        {quantity === null && (
                                          <p className="mt-1 text-xs text-gray-500">Unlimited</p>
                                        )}
                                        {quantity === 0 && (
                                          <p className="mt-1 text-xs font-medium text-red-500">
                                            Out of Stock
                                          </p>
                                        )}
                                      </div>
                                    </div>

                                    {/* Stock Level Progress Bar */}
                                    {quantity !== null && quantity > 0 && (
                                      <div className="mt-4">
                                        <div className="flex justify-between items-center mb-2">
                                          <span className="text-xs font-medium text-gray-600">
                                            Stock Level
                                          </span>
                                          <span className="text-xs font-semibold text-gray-700">
                                            {Math.min(100, Math.round((quantity / 1000) * 100))}%
                                          </span>
                                        </div>
                                        <div className="w-full h-2.5 bg-white/50 rounded-full overflow-hidden shadow-inner">
                                          <motion.div
                                            className={`h-full rounded-full shadow-sm bg-gradient-to-r ${
                                              stockStatus.color === "red"
                                                ? "from-red-500 to-red-600"
                                                : stockStatus.color === "orange"
                                                ? "from-orange-500 to-orange-600"
                                                : stockStatus.color === "yellow"
                                                ? "from-yellow-500 to-yellow-600"
                                                : stockStatus.color === "green"
                                                ? "from-green-500 to-green-600"
                                                : stockStatus.color === "brand"
                                                ? "from-[#FF6C6C] to-[#FF8F8F]"
                                                : "from-blue-500 to-blue-600"
                                            }`}
                                            initial={{ width: 0 }}
                                            animate={{
                                              width: `${Math.min(100, Math.round((quantity / 1000) * 100))}%`,
                                            }}
                                            transition={{ duration: 0.5, ease: "easeOut" }}
                                          />
                                        </div>
                                      </div>
                                    )}

                                    {/* Helpful Message */}
                                    <motion.div
                                      initial={{ opacity: 0 }}
                                      animate={{ opacity: 1 }}
                                      transition={{ delay: 0.2 }}
                                      className="flex gap-2 items-start p-3 mt-4 rounded-xl bg-white/60"
                                    >
                                      <Package className="w-4 h-4 text-gray-600 flex-shrink-0 mt-0.5" />
                                      <p className="text-xs leading-relaxed text-gray-700">
                                        {quantity === null
                                          ? "Unlimited availability is set. Quantity tracking is disabled for this deal."
                                          : quantity === 0
                                          ? "⚠️ Out of stock! This deal has no items available."
                                          : quantity < 10
                                          ? "Low stock alert! Limited items available. Act fast before they run out."
                                          : quantity < 50
                                          ? "Moderate stock available. Good selection still in store."
                                          : "Excellent stock levels! Plenty of items available for customers."}
                                      </p>
                                    </motion.div>
                                  </div>
                                );
                              })()}
                            </div>
                          ) : null}

                          {/* Delivery Information */}
                          {deal.delivery_fee && (
                            <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200">
                              <div className="flex items-center gap-2 mb-2">
                                <Package className="w-4 h-4 text-green-600" />
                                <h4 className="text-sm font-semibold text-gray-900">Delivery</h4>
                              </div>
                              <p className="text-sm text-gray-600">
                                {deal.delivery_fee === "not_apply" ? (
                                  <span className="text-green-700 font-medium">✓ No delivery fees apply</span>
                                ) : deal.delivery_fee === "free" ? (
                                  <span className="text-green-700 font-medium">✓ FREE Delivery</span>
                                ) : (
                                  <span>Delivery fees may apply</span>
                                )}
                              </p>
                              {deal.free_delivery_areas && deal.free_delivery_areas.length > 0 && (
                                <div className="mt-2">
                                  <p className="text-xs text-gray-500 mb-1">Free delivery areas:</p>
                                  <div className="flex flex-wrap gap-1">
                                    {deal.free_delivery_areas.map((area, idx) => (
                                      <span key={idx} className="text-xs bg-white px-2 py-1 rounded border border-green-200">
                                        {area}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Exclusions */}
                          {deal.exclusions && deal.exclusions.length > 0 && (
                            <div className="p-4 bg-gradient-to-br from-red-50 to-orange-50 rounded-xl border border-red-200">
                              <div className="flex items-center gap-2 mb-2">
                                <AlertCircle className="w-4 h-4 text-red-600" />
                                <h4 className="text-sm font-semibold text-red-900">Exclusions</h4>
                              </div>
                              <ul className="space-y-1">
                                {deal.exclusions.map((exclusion, idx) => (
                                  <li key={idx} className="text-sm text-red-700 flex items-start gap-2">
                                    <span className="text-red-500 mt-1">•</span>
                                    <span>{exclusion}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Age & Redemption Restrictions */}
                          {(deal.age || (deal.redemption_limits && deal.redemption_limits.length > 0)) && (
                            <div className="p-4 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-xl border border-indigo-200 shadow-sm">
                              <div className="flex gap-2 items-center mb-4">
                                <div className="w-2 h-2 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full"></div>
                                <h4 className="text-sm font-semibold text-indigo-800">
                                  Restrictions & Limits
                                </h4>
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                {/* Age Restrictions */}
                                {deal.age && (
                                  <div className="p-3 rounded-lg border border-indigo-100 bg-white/70">
                                    <div className="flex gap-2 items-center mb-2">
                                      <span className="text-xs font-medium text-indigo-700">
                                        Age Restrictions
                                      </span>
                                    </div>
                                    {/* Check if both min_age and max_age are null - means no age limit */}
                                    {deal.age.min_age === null && deal.age.max_age === null ? (
                                      <span className="text-sm font-medium text-green-700">
                                        No age restrictions
                                      </span>
                                    ) : (
                                      <div className="space-y-1">
                                        {deal.age.min_age !== null && deal.age.min_age !== undefined && (
                                          <div className="text-sm text-gray-700">
                                            Minimum age: <span className="font-semibold">{deal.age.min_age} years</span>
                                          </div>
                                        )}
                                        {deal.age.max_age !== null && deal.age.max_age !== undefined && (
                                          <div className="text-sm text-gray-700">
                                            Maximum age: <span className="font-semibold">{deal.age.max_age} years</span>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Redemption Limits */}
                                {deal.redemption_limits && deal.redemption_limits.length > 0 && (
                                  <div className="p-3 rounded-lg border border-purple-100 bg-white/70">
                                    <div className="flex gap-2 items-center mb-2">
                                      <span className="text-xs font-medium text-purple-700">
                                        Redemption Limits
                                      </span>
                                    </div>
                                    <div className="space-y-2">
                                      {deal.redemption_limits.map((limit, index) => (
                                        <div key={index} className="flex gap-2 items-center text-sm text-gray-700">
                                          <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                                          <span>{limit}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Validity */}
                          {deal.expiry_date && (
                            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                              <div className="flex justify-between items-center">
                                <div className="flex gap-2 items-center">
                                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                  <div>
                                    <span className="text-xs text-blue-500">
                                      {isExpired(deal.expiry_date) ? "Expired On" : "Valid Until"}
                                    </span>
                                    <div className="text-sm font-semibold text-blue-600">
                                      {moment(deal.expiry_date).format("DD MMM, YYYY")}
                                    </div>
                                  </div>
                                </div>
                                {!isExpired(deal.expiry_date) && (
                                  <div className="px-3 py-1 bg-blue-100 rounded-md">
                                    <span className="text-xs font-medium text-blue-600">
                                      {moment(deal.expiry_date).startOf('day').diff(moment().startOf('day'), "days")} days left
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </motion.div>
                      )}

                      {activeTab === "categories" && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="space-y-6"
                        >
                          <div className="p-6 bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-100 shadow-sm">
                            <div className="flex gap-3 items-center mb-6">
                              <div className="w-1 h-8 bg-gradient-to-b from-pink-500 to-purple-600 rounded-full" />
                              <h4 className="text-lg font-semibold text-gray-900">Categories</h4>
                            </div>

                            <div className="flex flex-wrap gap-2 md:gap-3">
                              {deal?.category?.map((category, index) => (
                                <motion.span
                                  key={index}
                                  initial={{ opacity: 0, scale: 0.95 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  transition={{ delay: index * 0.05 }}
                                  className="relative group px-4 py-2.5 bg-white border border-gray-200 
                                            text-gray-700 rounded-xl text-sm font-medium
                                            hover:border-transparent hover:text-white
                                            hover:bg-gradient-to-r from-pink-500 to-purple-600 
                                            transition-all duration-300 shadow-sm"
                                >
                                  {category}
                                </motion.span>
                              ))}
                            </div>
                          </div>
                        </motion.div>
                      )}

                      {activeTab === "contact" && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="space-y-4"
                        >
                          {/* Social Media */}
                          <div>
                            <h4 className="mb-2 text-xs sm:text-sm font-medium text-gray-700">Social Media</h4>
                            <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 gap-2">
                              {deal?.contact?.social_media?.instagram && (
                                <a
                                  href={deal.contact.social_media.instagram}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex gap-2 justify-center items-center px-4 py-3 text-white bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg"
                                >
                                  <Instagram className="w-5 h-5" />
                                  <span className="text-sm font-medium">Instagram</span>
                                </a>
                              )}
                              {deal?.contact?.social_media?.facebook && (
                                <a
                                  href={deal.contact.social_media.facebook}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex gap-2 justify-center items-center px-4 py-3 text-white bg-gradient-to-r from-blue-600 to-blue-500 rounded-lg"
                                >
                                  <Facebook className="w-5 h-5" />
                                  <span className="text-sm font-medium">Facebook</span>
                                </a>
                              )}
                              {deal?.contact?.social_media?.twitter && (
                                <a
                                  href={deal.contact.social_media.twitter}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex gap-2 justify-center items-center px-4 py-3 text-white bg-black rounded-lg"
                                >
                                  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                  </svg>
                                  <span className="text-sm font-medium">Twitter</span>
                                </a>
                              )}
                            </div>
                          </div>

                          {/* Contact Details */}
                          <div>
                            <h4 className="mb-2 text-sm font-medium text-gray-700">Contact Details</h4>
                            <div className="space-y-2">
                              {deal?.contact?.phone_number && (
                                <div className="flex gap-3 items-center p-3 bg-white rounded-lg border">
                                  <div className="p-2 bg-pink-100 rounded-md">
                                    <Phone className="w-5 h-5 text-pink-600" />
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500">Phone Number</p>
                                    <a href={`tel:${deal.contact.phone_number}`} className="text-sm font-medium text-gray-900">
                                      {deal.contact.phone_number}
                                    </a>
                                  </div>
                                </div>
                              )}
                              {deal?.contact?.email && (
                                <div className="flex gap-3 items-center p-3 bg-white rounded-lg border">
                                  <div className="p-2 bg-blue-100 rounded-md">
                                    <Mail className="w-5 h-5 text-blue-600" />
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500">Email</p>
                                    <a href={`mailto:${deal.contact.email}`} className="text-sm font-medium text-gray-900">
                                      {deal.contact.email}
                                    </a>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      )}

                      {activeTab === "locations" && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="space-y-6"
                        >
                          {/* Target Locations */}
                          <div>
                            <div className="flex gap-3 items-center mb-4">
                              <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-blue-600 rounded-full" />
                              <h4 className="text-lg font-semibold text-gray-900">Target Locations</h4>
                            </div>
                            <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 shadow-sm">
                              {deal?.region_location?.length > 0 && 
                               deal.region_location.some(locale => locale.region === "all" && locale.location === "all") ? (
                                <div className="p-3 bg-white rounded-lg border border-blue-100">
                                  <div className="flex gap-3 items-start">
                                    <div className="flex-shrink-0 mt-2 w-2 h-2 bg-blue-500 rounded-full" />
                                    <div>
                                      <h6 className="font-medium text-gray-900">Nationwide</h6>
                                      <div className="flex gap-2 items-center text-sm text-gray-600">
                                        <Split className="w-4 h-4 text-blue-500" />
                                        <span>All Regions</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                  {deal?.region_location?.map((locale, index) => (
                                    <div key={index} className="p-3 bg-white rounded-lg border border-blue-100">
                                      <div className="flex gap-3 items-start">
                                        <div className="flex-shrink-0 mt-2 w-2 h-2 bg-blue-500 rounded-full" />
                                        <div>
                                          <h6 className="font-medium text-gray-900">{locale.location}</h6>
                                          <div className="flex gap-2 items-center text-sm text-gray-600">
                                            <Split className="w-4 h-4 text-blue-500" />
                                            <span>{locale.region}</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Redemption Locations */}
                          <div>
                            <div className="flex gap-3 items-center mb-4">
                              <div className="w-1 h-8 bg-gradient-to-b from-green-500 to-green-600 rounded-full" />
                              <h4 className="text-lg font-semibold text-gray-900">Redemption Locations</h4>
                            </div>
                            <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 shadow-sm">
                              {deal?.redemption_region_location?.length > 0 && 
                               deal.redemption_region_location.some(locale => locale.region === "all" && locale.location === "all") ? (
                                <div className="p-3 bg-white rounded-lg border border-green-100">
                                  <div className="flex gap-3 items-start">
                                    <div className="flex-shrink-0 mt-2 w-2 h-2 bg-green-500 rounded-full" />
                                    <div>
                                      <h6 className="font-medium text-gray-900">Nationwide</h6>
                                      <div className="flex gap-2 items-center text-sm text-gray-600">
                                        <Split className="w-4 h-4 text-green-500" />
                                        <span>All Regions</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                  {deal?.redemption_region_location?.map((locale, index) => (
                                    <div key={index} className="p-3 bg-white rounded-lg border border-green-100">
                                      <div className="flex gap-3 items-start">
                                        <div className="flex-shrink-0 mt-2 w-2 h-2 bg-green-500 rounded-full" />
                                        <div>
                                          <h6 className="font-medium text-gray-900">{locale.location}</h6>
                                          <div className="flex gap-2 items-center text-sm text-gray-600">
                                            <Split className="w-4 h-4 text-green-500" />
                                            <span>{locale.region}</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      )}

                      {activeTab === "redemption" && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="space-y-4"
                        >
                          {/* Redemption Type */}
                          {deal.redemption_type && deal.redemption_type.length > 0 && (
                            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                              <h4 className="mb-2 text-xs sm:text-sm font-medium text-blue-700">Redemption Type</h4>
                              <div className="flex flex-wrap gap-2">
                                {deal.redemption_type.map((type, index) => (
                                  <span key={index} className="inline-flex items-center gap-1.5 px-2 sm:px-3 py-1 text-xs sm:text-sm text-blue-700 bg-blue-100 rounded-full">
                                    {type === "Online" ? <Globe className="w-3 h-3 sm:w-4 sm:h-4" /> : <Store className="w-3 h-3 sm:w-4 sm:h-4" />}
                                    {type}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </motion.div>
                      )}

                      {activeTab === "terms" && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="space-y-6"
                        >
                          {/* Terms & Conditions */}
                          <div className="p-6 bg-gradient-to-br from-blue-50 to-white rounded-2xl border border-blue-100">
                            <div className="flex gap-3 items-center mb-4">
                              <div className="p-2 bg-blue-100 rounded-lg">
                                <ScrollText className="w-5 h-5 text-blue-600" />
                              </div>
                              <h3 className="text-lg font-semibold text-gray-900">Terms & Conditions</h3>
                            </div>
                            <div className="prose prose-sm max-w-none">
                              <pre className="font-sans text-sm leading-relaxed text-gray-600 whitespace-pre-wrap">
                                {deal?.terms}
                              </pre>
                            </div>
                            <div className="flex gap-2 items-center p-3 mt-4 rounded-lg border border-blue-100 bg-blue-50/50">
                              <Info className="w-4 h-4 text-blue-600" />
                              <span className="text-sm text-blue-600">
                                These terms are subject to change. Last updated {moment().format("MMMM DD, YYYY")}
                              </span>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 flex gap-2 sm:gap-3 justify-end items-center p-3 sm:p-4 md:p-6 bg-gray-50 border-t">
              <button
                onClick={onClose}
                className="px-4 sm:px-6 py-2 text-sm sm:text-base text-gray-600 rounded-xl transition-colors duration-200 hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </motion.div>

          {/* Full-Screen Image Gallery Modal */}
          <AnimatePresence>
            {showImageGallery && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/90 z-[2000] backdrop-blur-sm"
                  onClick={() => setShowImageGallery(false)}
                />
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="fixed inset-0 z-[2001] flex items-center justify-center p-2 sm:p-4"
                >
                  <div className="relative max-w-4xl max-h-[90vh] w-full">
                    <button
                      onClick={() => setShowImageGallery(false)}
                      className="absolute top-2 right-2 sm:top-4 sm:right-4 z-10 p-1.5 sm:p-2 text-white rounded-full transition-colors bg-black/50 hover:bg-black/70"
                    >
                      <X className="w-5 h-5 sm:w-6 sm:h-6" />
                    </button>

                    <div className="overflow-hidden relative bg-white rounded-lg">
                      <img
                        src={getAllImages()[currentImageIndex]}
                        alt={`Gallery ${currentImageIndex + 1}`}
                        className="w-full h-auto max-h-[80vh] object-contain"
                      />
                    </div>

                    {getAllImages().length > 1 && (
                      <>
                        <button
                          onClick={prevImage}
                          className="absolute left-2 sm:left-4 top-1/2 p-1.5 sm:p-2 text-white rounded-full transition-colors -translate-y-1/2 bg-black/50 hover:bg-black/70"
                        >
                          <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
                        </button>
                        <button
                          onClick={nextImage}
                          className="absolute right-2 sm:right-4 top-1/2 p-1.5 sm:p-2 text-white rounded-full transition-colors -translate-y-1/2 bg-black/50 hover:bg-black/70"
                        >
                          <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
                        </button>
                        <div className="absolute bottom-2 sm:bottom-4 left-1/2 px-2 sm:px-3 py-1 text-xs sm:text-sm text-white rounded-full -translate-x-1/2 bg-black/50">
                          {currentImageIndex + 1} / {getAllImages().length}
                        </div>
                      </>
                    )}
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </>
      )}
    </AnimatePresence>
  );
};

export default RedemptionDealDetailsSlider;

